import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useReviewerCapabilities(workspaceId: string) {
  return useQuery({
    queryKey: ['reviewer-capabilities', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviewer_capabilities')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('quality_score', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useUpdateReviewerCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, workspaceId, updates }: { 
      userId: string; 
      workspaceId: string; 
      updates: any 
    }) => {
      const { data, error } = await supabase
        .from('reviewer_capabilities')
        .upsert({
          user_id: userId,
          workspace_id: workspaceId,
          ...updates
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewer-capabilities'] });
      toast.success('Reviewer capabilities updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update capabilities: ' + error.message);
    }
  });
}

export function useIntelligentAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      workspaceId: string;
      vendorId?: string;
      commodityType?: string;
      assignmentStrategy?: 'round_robin' | 'skill_based' | 'workload_balanced';
    }) => {
      const { data, error } = await supabase.functions.invoke('intelligent-task-assignment', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['reviewer-capabilities'] });
      toast.success('Task assigned automatically');
    },
    onError: (error: Error) => {
      toast.error('Failed to assign task: ' + error.message);
    }
  });
}

export function useAssignmentHistory(taskId: string) {
  return useQuery({
    queryKey: ['assignment-history', taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_assignment_history')
        .select('*')
        .eq('task_id', taskId)
        .order('assigned_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!taskId
  });
}
